import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/spacem8-logo.png";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";
import { logActivity } from "../lib/auditLog";
import {
  askPortfolioAssistant,
  buildPortfolioAssistantPayload,
  inferPortfolioPromptScope,
  minimizePortfolioPayload,
} from "../lib/portfolioAssistant";
import { loadAiPrivacySettings, resolveAiPrivacyMode } from "../lib/aiPrivacy";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertTriangle,
  BrainCircuit,
  Building2,
  ChartNoAxesCombined,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  FileText,
  House,
  Lightbulb,
  MessageSquareMore,
  Moon,
  Search,
  Send,
  Settings,
  Sparkles,
  Sun,
  TrendingUp,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function buildResultPages(items) {
  const pages = [];
  let pendingUser = null;

  items.forEach((item) => {
    if (item.type === "user") {
      pendingUser = item;
      return;
    }

    if (item.type === "assistant") {
      pages.push({
        id: item.id,
        user: pendingUser,
        assistant: item,
        title: pendingUser?.text || "Portfolio insight",
      });
      pendingUser = null;
    }
  });

  if (pendingUser) {
    pages.push({
      id: pendingUser.id,
      user: pendingUser,
      assistant: null,
      title: pendingUser.text,
    });
  }

  return pages;
}

const navItems = [
  { label: "Dashboard", icon: House, path: "/" },
  { label: "Properties", icon: Building2, path: "/properties" },
  { label: "Tenants", icon: Users, path: "/tenants" },
  { label: "Payments", icon: Wallet, path: "/payments" },
  { label: "Invoices", icon: FileText, path: "/invoices" },
  { label: "Maintenance", icon: Wrench, path: "/maintenance" },
  { label: "Messages", icon: MessageSquareMore, path: "/messages" },
  { label: "Reports", icon: ChartNoAxesCombined, path: "/reports" },
  { label: "AI Assistant", icon: BrainCircuit, path: "/ai-assistant" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

const suggestions = [
  "Which property needs urgent attention this week?",
  "Summarize rent collection performance this month",
  "Draft a payment reminder for pending tenants",
  "Which maintenance issue has the highest operational risk?",
  "Give me an executive summary of the portfolio",
];

const aiResponses = [
  {
    id: 1,
    type: "assistant",
    text: "I’ve reviewed your current portfolio signals. Revenue is stable overall, but Urban Crest Apartments requires immediate management attention because of late collections and maintenance drag.",
  },
  {
    id: 2,
    type: "assistant",
    text: "My recommendation is to send targeted payment reminders, resolve open maintenance tickets affecting tenant experience, and review renewal incentives for at-risk units.",
  },
];

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency, "Ksh 0");
}

function buildPortfolioSummary(data) {
  const totalUnits = data.properties.reduce((sum, item) => sum + Number(item.units || 0), 0);
  const occupiedUnits = data.properties.reduce((sum, item) => sum + Number(item.occupied || 0), 0);
  const portfolioRevenue = data.properties.reduce(
    (sum, item) => sum + Number(item.rent_per_unit || 0) * Number(item.occupied || 0),
    0
  );
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
  const paidInvoices = data.invoices
    .filter((item) => item.status === "Paid")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const invoiceTotal = data.invoices.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const pendingPayments = data.payments
    .filter((item) => item.status === "Pending")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const overduePayments = data.payments
    .filter((item) => item.status === "Overdue")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const openTickets = data.tickets.filter((item) => item.status !== "Completed");
  const highPriorityTickets = openTickets.filter((item) => item.priority === "High");
  const collectionsEfficiency = invoiceTotal > 0 ? Math.round((paidInvoices / invoiceTotal) * 100) : 0;

  const priorityProperty = [...data.properties].sort((a, b) => {
    const aOcc = Number(a.units || 0) > 0 ? Number(a.occupied || 0) / Number(a.units || 0) : 0;
    const bOcc = Number(b.units || 0) > 0 ? Number(b.occupied || 0) / Number(b.units || 0) : 0;
    const aScore = Number(a.maintenance_open || 0) + (1 - aOcc) * 10;
    const bScore = Number(b.maintenance_open || 0) + (1 - bOcc) * 10;

    return bScore - aScore;
  })[0];

  return {
    totalProperties: data.properties.length,
    totalTenants: data.tenants.length,
    portfolioRevenue,
    occupancyRate,
    collectionsEfficiency,
    pendingPayments,
    overduePayments,
    openTickets,
    highPriorityTickets,
    priorityProperty,
  };
}

function buildDraftReminder(topOverdueInvoice, currency) {
  if (!topOverdueInvoice) {
    return "Hello, this is a friendly reminder that your rent/payment is pending. Please complete payment as soon as possible, or contact management if you need help confirming your balance. Thank you.";
  }

  return `Hello ${topOverdueInvoice.tenant}, this is a friendly reminder that ${formatMoney(
    topOverdueInvoice.balance,
    currency
  )} is still outstanding for ${topOverdueInvoice.property}. Please arrange payment as soon as possible, or contact management if you need help confirming your balance. Thank you.`;
}

function getDaysPastDue(dueDate) {
  if (!dueDate) return 0;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.floor((startOfToday.getTime() - due.getTime()) / 86400000));
}

function buildCollectionsSnapshot(data) {
  const paidByInvoice = data.payments.reduce((totalsByInvoice, payment) => {
    if (payment.invoice_id && payment.status === "Paid") {
      totalsByInvoice[payment.invoice_id] =
        (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
    }

    return totalsByInvoice;
  }, {});

  return data.invoices
    .map((invoice) => {
      const amount = Number(invoice.amount || 0);
      const paidAmount =
        invoice.status === "Paid" && !paidByInvoice[invoice.id]
          ? amount
          : Number(paidByInvoice[invoice.id] || 0);

      return {
        id: invoice.id,
        tenant: invoice.tenant || "Unknown tenant",
        property: invoice.property || "Unassigned property",
        dueDate: invoice.due_date || "",
        status: invoice.status,
        balance: Math.max(amount - paidAmount, 0),
        daysPastDue: getDaysPastDue(invoice.due_date),
      };
    })
    .filter((invoice) => invoice.balance > 0)
    .sort((a, b) => b.daysPastDue - a.daysPastDue || b.balance - a.balance);
}

function buildAssistantResult(prompt, data, summary, currency) {
  const normalized = prompt.toLowerCase();
  const priorityName = summary.priorityProperty?.name || "No property available";
  const highRiskTicket = summary.highPriorityTickets[0];
  const overdueInvoices = buildCollectionsSnapshot(data).filter((invoice) => invoice.daysPastDue > 0);
  const topOverdueInvoice = overdueInvoices[0];
  const reminderDraft = buildDraftReminder(topOverdueInvoice, currency);

  if (normalized.includes("maintenance") || normalized.includes("risk")) {
    if (!summary.openTickets.length) {
      return {
        text: "There are no open maintenance tickets right now. Operational risk is low from the maintenance queue.",
        followUp: {
          title: "Review current property health",
          detail: `Check ${priorityName} and confirm whether any non-maintenance operational issues still need attention.`,
        },
        actions: [
          {
            id: "review-property-health",
            label: "Review property health",
            description: "Open the properties workspace and inspect the current priority asset.",
            icon: Building2,
            path: "/properties",
            state: { initialQuery: priorityName },
          },
        ],
      };
    }

    return {
      text: `The highest operational risk is ${highRiskTicket?.issue || summary.openTickets[0].issue} at ${
        highRiskTicket?.property || summary.openTickets[0].property
      }. There are ${summary.openTickets.length} open tickets, including ${
        summary.highPriorityTickets.length
      } high-priority item${summary.highPriorityTickets.length === 1 ? "" : "s"}. I would prioritize high-priority tickets first, then anything affecting occupied units.`,
      followUp: {
        title: `Escalate maintenance at ${highRiskTicket?.property || summary.openTickets[0].property}`,
        detail: `Review the highest-risk ticket and confirm an owner, next step, and timing for resolution.`,
      },
      actions: [
        {
          id: "open-maintenance-queue",
          label: "Open maintenance queue",
          description: "Jump into a focused ticket list for the highest-risk issue.",
          icon: Wrench,
          path: "/maintenance",
          state: {
            initialQuery: highRiskTicket?.property || summary.openTickets[0].property,
            initialEscalationsOnly: true,
          },
        },
        {
          id: "review-risk-property",
          label: "Review property",
          description: "Inspect the property connected to the top maintenance risk.",
          icon: Building2,
          path: "/properties",
          state: {
            initialQuery: highRiskTicket?.property || summary.openTickets[0].property,
          },
        },
      ],
    };
  }

  if (normalized.includes("collection") || normalized.includes("payment") || normalized.includes("rent")) {
    return {
      text: `Collections efficiency is ${summary.collectionsEfficiency}%. Pending payments total ${formatMoney(summary.pendingPayments, currency)}, and overdue payments total ${formatMoney(summary.overduePayments, currency)}. I would follow up on overdue balances first, then send reminders for pending invoices before their due dates.`,
      followUp: {
        title: "Follow up on overdue rent balances",
        detail: topOverdueInvoice
          ? `Prioritize ${topOverdueInvoice.tenant} at ${topOverdueInvoice.property} and confirm reminder outreach today.`
          : "Review overdue balances and confirm which tenants should receive a reminder first.",
      },
      actions: [
        {
          id: "draft-reminder",
          label: "Draft reminder",
          description: "Open Messages with a prefilled reminder for the top overdue tenant.",
          icon: MessageSquareMore,
          path: "/messages",
          state: {
            draftMessage: {
              draftType: "payment-reminder",
              contactName: topOverdueInvoice?.tenant || "",
              contactRole: topOverdueInvoice ? `Tenant - ${topOverdueInvoice.property}` : "Tenant",
              body: reminderDraft,
            },
          },
        },
        {
          id: "open-overdue-invoices",
          label: "Open overdue invoices",
          description: "Review the most urgent invoice balances first.",
          icon: FileText,
          path: "/invoices",
          state: {
            initialStatusFilter: "Overdue",
            initialQuery: topOverdueInvoice?.tenant || topOverdueInvoice?.property || "",
          },
        },
      ],
    };
  }

  if (normalized.includes("reminder") || normalized.includes("draft")) {
    return {
      text: `Draft reminder:\n\n${reminderDraft}`,
      followUp: {
        title: "Review and send AI payment reminder",
        detail: topOverdueInvoice
          ? `Confirm the reminder for ${topOverdueInvoice.tenant} and send it from the Messages workspace.`
          : "Review the drafted reminder and send it from the Messages workspace.",
      },
      actions: [
        {
          id: "open-reminder-draft",
          label: "Open draft in messages",
          description: "Start from a prefilled reminder and send it after review.",
          icon: MessageSquareMore,
          path: "/messages",
          state: {
            draftMessage: {
              draftType: "payment-reminder",
              contactName: topOverdueInvoice?.tenant || "",
              contactRole: topOverdueInvoice ? `Tenant - ${topOverdueInvoice.property}` : "Tenant",
              body: reminderDraft,
            },
          },
        },
        {
          id: "check-overdue-accounts",
          label: "Check overdue accounts",
          description: "Open the invoice queue filtered to the current arrears priority.",
          icon: FileText,
          path: "/invoices",
          state: {
            initialStatusFilter: "Overdue",
            initialQuery: topOverdueInvoice?.tenant || topOverdueInvoice?.property || "",
          },
        },
      ],
    };
  }

  if (normalized.includes("property") || normalized.includes("attention")) {
    return {
      text: `${priorityName} needs the most attention based on occupancy and maintenance pressure. Current portfolio occupancy is ${summary.occupancyRate}%, with ${summary.totalProperties} properties and ${summary.totalTenants} tenants tracked in Supabase.`,
      followUp: {
        title: `Review performance plan for ${priorityName}`,
        detail: "Check occupancy drag, maintenance pressure, and the next operational action for this property.",
      },
      actions: [
        {
          id: "open-priority-property",
          label: "Open property",
          description: "Jump straight into the property that needs attention.",
          icon: Building2,
          path: "/properties",
          state: { initialQuery: priorityName },
        },
        {
          id: "review-related-maintenance",
          label: "Review related maintenance",
          description: "Open maintenance items linked to the priority property.",
          icon: Wrench,
          path: "/maintenance",
          state: {
            initialQuery: priorityName,
            initialEscalationsOnly: true,
          },
        },
      ],
    };
  }

  return {
    text: `Executive summary: portfolio revenue is ${formatMoney(summary.portfolioRevenue, currency)}, occupancy is ${
      summary.occupancyRate
    }%, collections efficiency is ${summary.collectionsEfficiency}%, and there are ${
      summary.openTickets.length
    } open maintenance tickets. The current priority property is ${priorityName}.`,
    followUp: {
      title: "Review portfolio priorities from AI summary",
      detail: `Validate the priority property, collections risk, and maintenance queue against today's management plan.`,
    },
    actions: [
      {
        id: "open-portfolio-reports",
        label: "Open reports",
        description: "Review the wider performance picture behind this summary.",
        icon: ChartNoAxesCombined,
        path: "/reports",
      },
      {
        id: "review-priority-property-summary",
        label: "Review priority property",
        description: "Inspect the asset with the highest current pressure.",
        icon: Building2,
        path: "/properties",
        state: { initialQuery: priorityName },
      },
    ],
  };
}

function buildDynamicInsights(summary, currency) {
  return [
    {
      title: "Collections position",
      description: `Collections efficiency is ${summary.collectionsEfficiency}%, with ${formatMoney(summary.pendingPayments, currency)} pending and ${formatMoney(summary.overduePayments, currency)} overdue.`,
      badge: summary.overduePayments > 0 ? "Follow up" : "Stable",
      icon: AlertTriangle,
      tone: summary.overduePayments > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700",
    },
    {
      title: "Portfolio momentum",
      description: `${summary.totalProperties} properties are active with ${summary.occupancyRate}% occupancy and ${formatMoney(summary.portfolioRevenue, currency)} projected monthly revenue.`,
      badge: "Live signal",
      icon: TrendingUp,
      tone: "bg-emerald-100 text-emerald-700",
    },
    {
      title: "Recommended next move",
      description: summary.highPriorityTickets.length
        ? `Resolve ${summary.highPriorityTickets.length} high-priority maintenance ticket${summary.highPriorityTickets.length === 1 ? "" : "s"} before routine tasks.`
        : "Keep monitoring collections and tenant satisfaction; no high-priority maintenance is open.",
      badge: "Suggested action",
      icon: Lightbulb,
      tone: "bg-sky-100 text-sky-700",
    },
  ];
}

function ResultCard({ item, isDark, onAction, onSaveFollowUp, isFollowUpSaved }) {
  return (
    <motion.div
      key={item.id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`rounded-3xl border p-4 ${
        item.type === "user"
          ? isDark
            ? "border-orange-900 bg-orange-950/30"
            : "border-orange-100 bg-orange-50"
          : isDark
          ? "border-slate-800 bg-slate-950/60"
          : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
      }`}
    >
      <div className={`mb-2 flex items-center gap-2 ${item.type === "user" ? "text-orange-600" : "text-sky-700"}`}>
        {item.type === "user" ? <Users className="h-5 w-5" /> : <BrainCircuit className="h-5 w-5" />}
        <span className="font-medium">{item.type === "user" ? "You" : "SpaceM8 AI"}</span>
      </div>

      {item.type === "assistant" && item.groundedSections?.length ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {item.privacyMode ? (
            <Badge className="bg-slate-100 text-slate-700">Mode: {item.privacyMode}</Badge>
          ) : null}
          {item.promptScope ? (
            <Badge className="bg-sky-100 text-sky-700">Scope: {item.promptScope}</Badge>
          ) : null}
          {item.groundedSections.map((section) => (
            <Badge key={section} className="bg-emerald-100 text-emerald-700">
              Grounded in {section}
            </Badge>
          ))}
        </div>
      ) : null}

      <p className={`whitespace-pre-line text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
        {item.text}
      </p>

      {item.type === "assistant" && item.actions?.length ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {item.actions.map((action) => {
            const Icon = action.icon;

            return (
              <button
                key={action.id}
                type="button"
                onClick={() => onAction(action, item)}
                className={`rounded-2xl border p-4 text-left transition ${
                  isDark
                    ? "border-slate-700 bg-slate-900 hover:bg-slate-800"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {action.label}
                    </p>
                    <p className={`mt-1 text-sm leading-6 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      {action.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : null}

      {item.type === "assistant" && item.followUp ? (
        <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-dashed border-sky-200 bg-sky-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{item.followUp.title}</p>
            <p className="mt-1 text-sm text-slate-600">{item.followUp.detail}</p>
            {item.includedSections?.length ? (
              <p className="mt-2 text-xs text-slate-500">
                Shared with AI: {item.includedSections.join(", ")}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            disabled={isFollowUpSaved}
            onClick={() => onSaveFollowUp(item)}
            className={`shrink-0 rounded-2xl px-4 py-2 text-sm font-medium transition ${
              isFollowUpSaved
                ? "cursor-not-allowed bg-emerald-100 text-emerald-700"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {isFollowUpSaved ? "Saved" : "Save follow-up"}
          </button>
        </div>
      ) : null}
    </motion.div>
  );
}

function Sidebar({ isDark, summary }) {
  return (
    <aside
      className={`hidden xl:flex xl:w-72 xl:flex-col xl:rounded-[2rem] xl:border xl:p-5 xl:shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-6 px-2">
        <div
          className={`flex items-center justify-center rounded-3xl border px-6 py-6 shadow-sm transition-all hover:shadow-md ${
            isDark
              ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
              : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
          }`}
        >
          <img
            src={logo}
            alt="SpaceM8"
            className="h-20 w-auto cursor-pointer object-contain transition-all duration-300 hover:scale-110"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        <p className={`mt-4 text-center text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          Executive Intelligence Layer
        </p>
      </div>

      <nav className="mt-1 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                  isActive
                    ? isDark
                      ? "bg-slate-800 text-white ring-1 ring-slate-700"
                      : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${
                      isActive
                        ? isDark
                          ? "text-sky-400"
                          : "text-sky-700"
                        : isDark
                        ? "text-slate-500"
                        : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div
        className={`mt-6 rounded-3xl border p-5 ${
          isDark
            ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
            : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
        }`}
      >
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          AI Summary
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Critical signal</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {summary?.highPriorityTickets?.length || 0} urgent tickets
            </p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Priority property</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {summary?.priorityProperty?.name || "No data yet"}
            </p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Mode</p>
            <p className="text-lg font-semibold text-sky-600">Executive advisory</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function AIAssistantPage({ isDark, setIsDark, currency, setCurrency }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(true);
  const [analysisData, setAnalysisData] = useState({
    properties: [],
    tenants: [],
    payments: [],
    invoices: [],
    tickets: [],
  });
  const [responses, setResponses] = useState(aiResponses);
  const [activeResultId, setActiveResultId] = useState(aiResponses[aiResponses.length - 1]?.id);
  const [savedFollowUps, setSavedFollowUps] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchAnalysisData = async () => {
    setLoading(true);

    const [
      { data: propertiesData, error: propertiesError },
      { data: tenantsData, error: tenantsError },
      { data: paymentsData, error: paymentsError },
      { data: invoicesData, error: invoicesError },
      { data: ticketsData, error: ticketsError },
    ] = await Promise.all([
      supabase.from("properties").select("*"),
      supabase.from("tenants").select("*"),
      supabase.from("payments").select("*"),
      supabase.from("invoices").select("*"),
      supabase.from("maintenance_tickets").select("*"),
    ]);

    if (propertiesError) console.error("AI properties fetch error:", propertiesError);
    if (tenantsError) console.error("AI tenants fetch error:", tenantsError);
    if (paymentsError) console.error("AI payments fetch error:", paymentsError);
    if (invoicesError) console.error("AI invoices fetch error:", invoicesError);
    if (ticketsError) console.error("AI maintenance fetch error:", ticketsError);

    setAnalysisData({
      properties: propertiesData || [],
      tenants: tenantsData || [],
      payments: paymentsData || [],
      invoices: invoicesData || [],
      tickets: ticketsData || [],
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  useEffect(() => {
    const quickPrompt = location.state?.quickPrompt;

    if (quickPrompt) {
      setPrompt(quickPrompt);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const summary = useMemo(() => buildPortfolioSummary(analysisData), [analysisData]);
  const dynamicInsights = useMemo(() => buildDynamicInsights(summary, currency), [summary, currency]);
  const resultPages = useMemo(() => buildResultPages(responses), [responses]);
  const activeResultIndex = Math.max(
    0,
    resultPages.findIndex((page) => page.id === activeResultId)
  );
  const activeResultPage = resultPages[activeResultIndex] || resultPages[resultPages.length - 1] || null;
  const aiPrivacySettings = useMemo(() => loadAiPrivacySettings(), []);
  const aiPrivacyMode = useMemo(
    () => resolveAiPrivacyMode(aiPrivacySettings, profile?.role),
    [aiPrivacySettings, profile?.role]
  );

  const handleRun = async (e) => {
    e.preventDefault();

    if (!prompt.trim() || isGenerating) return;

    const userPrompt = prompt.trim();
    const userResponseId = `user-${Date.now()}`;
    const assistantResponseId = `assistant-${Date.now()}`;
    const result = buildAssistantResult(userPrompt, analysisData, summary, currency);
    const fullPortfolio = buildPortfolioAssistantPayload(analysisData, summary, currency, {
      maskCustomerData: aiPrivacyMode.maskCustomerData,
      highPrivacyMode: aiPrivacyMode.highPrivacyMode,
      mode: aiPrivacyMode.mode,
    });
    const promptScope = inferPortfolioPromptScope(userPrompt);
    const portfolio = minimizePortfolioPayload(fullPortfolio, promptScope);
    let assistantText = result.text;
    let groundedSections = [];

    setIsGenerating(true);

    if (aiPrivacyMode.useExternalAi) {
      try {
        const aiResponse = await askPortfolioAssistant({
          prompt: userPrompt,
          portfolio,
        });

        assistantText = aiResponse.answer;
        groundedSections = aiResponse.grounded_sections || [];
      } catch (error) {
        console.error("SpaceM8 AI invoke error:", error);
        toast.info(
          "Using local assistant mode",
          "The secure AI service is not available yet, so SpaceM8 used the in-app fallback logic."
        );
      }
    } else {
      toast.info(
        "External AI processing is disabled",
        "SpaceM8 answered using local in-app logic only because AI data sharing is turned off."
      );
    }

    setResponses((prev) => [
      ...prev,
      {
        id: userResponseId,
        type: "user",
        text: userPrompt,
      },
      {
        id: assistantResponseId,
        type: "assistant",
        text: assistantText,
        prompt: userPrompt,
        actions: result.actions,
        followUp: result.followUp,
        groundedSections,
        privacyMode: aiPrivacyMode.mode,
        promptScope,
        includedSections: portfolio.includedSections,
      },
    ]);
    setActiveResultId(assistantResponseId);
    setPrompt("");
    setIsGenerating(false);
  };

  const handleAction = async (action, response) => {
    await logActivity({
      action: "Ran AI action",
      entityType: "ai_action",
      entityLabel: action.label,
      details: {
        targetPath: action.path,
        prompt: response.prompt || "",
        recommendation: response.text,
        privacyMode: response.privacyMode || aiPrivacyMode.mode,
      },
    });

    navigate(action.path, { state: action.state });
  };

  const handleSaveFollowUp = async (response) => {
    if (!response.followUp || savedFollowUps[response.id]) return;

    await logActivity({
      action: "Saved AI follow-up",
      entityType: "ai_follow_up",
      entityLabel: response.followUp.title,
      details: {
        prompt: response.prompt || "",
        recommendation: response.text,
        followUp: response.followUp,
        privacyMode: response.privacyMode || aiPrivacyMode.mode,
        suggestedActions: (response.actions || []).map((action) => ({
          label: action.label,
          path: action.path,
        })),
      },
    });

    setSavedFollowUps((prev) => ({
      ...prev,
      [response.id]: true,
    }));
    toast.success("Follow-up saved", "The AI follow-up was added to recent activity.");
  };

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 ${
            isDark
              ? "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.08),_transparent_22%),linear-gradient(to_bottom_right,_#020617,_#0f172a,_#020617)]"
              : "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_24%),linear-gradient(to_bottom_right,_#f8fbff,_#f6f9fc,_#eef5fb)]"
          }`}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1600px] gap-6 p-4 md:p-6 xl:p-8">
        <Sidebar isDark={isDark} summary={summary} />

        <main className="flex-1">
          <motion.header
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-[2rem] border p-5 shadow-sm ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sky-700">
                  <Sparkles className="h-4 w-4" />
                  <p className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Portfolio Intelligence
                  </p>
                </div>
                <h1 className={`mt-2 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  AI Assistant
                </h1>
                <p className={`mt-2 max-w-2xl text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Ask questions, generate recommendations, detect portfolio risks, and get executive guidance across the platform.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className={`h-12 rounded-2xl px-4 ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => setIsDark(!isDark)}
                >
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? "Light" : "Dark"}
                </Button>

                <Button onClick={fetchAnalysisData} className="h-12 rounded-2xl px-5">
                  <BrainCircuit className="mr-2 h-4 w-4" />
                  {loading ? "Loading..." : "Refresh Analysis"}
                </Button>
              </div>
            </div>
          </motion.header>

          <section className="mt-6 grid gap-6 2xl:grid-cols-[1.2fr_0.8fr]">
            <div
              className={`rounded-[2rem] border p-5 shadow-md ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="mb-5">
                <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  Ask SpaceM8 AI
                </h2>
                <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
                  Run portfolio analysis, generate communication drafts, and surface key operational actions.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge className="bg-sky-100 text-sky-700">
                    AI mode: {aiPrivacyMode.mode}
                  </Badge>
                  {aiPrivacyMode.maskCustomerData ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Customer data masked</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">Full detail enabled</Badge>
                  )}
                  {aiPrivacyMode.highPrivacyMode ? (
                    <Badge className="bg-emerald-100 text-emerald-700">High privacy reduction on</Badge>
                  ) : null}
                </div>
              </div>

              <form onSubmit={handleRun} className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Ask something like: Which property needs attention this week?"
                    className={`h-12 pl-11 pr-4 ${
                      isDark
                        ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                        : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                    }`}
                  />
                </div>

                <Button type="submit" disabled={loading} className="h-12 rounded-2xl px-5">
                  <Send className="mr-2 h-4 w-4" />
                  {isGenerating ? "Thinking..." : loading ? "Loading..." : "Run"}
                </Button>
              </form>

              <div className="mt-5 flex flex-wrap gap-2">
                {suggestions.map((item) => (
                  <button
                    key={item}
                    onClick={() => setPrompt(item)}
                    className={`rounded-2xl px-4 py-2 text-sm transition ${
                      isDark
                        ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>

              <div
                className={`mt-6 rounded-3xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      Result Pages
                    </p>
                    <h3 className={`mt-1 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {activeResultPage?.title || "No assistant results yet"}
                    </h3>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={activeResultIndex <= 0}
                      onClick={() => setActiveResultId(resultPages[activeResultIndex - 1]?.id)}
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className={`min-w-[88px] text-center text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      {resultPages.length ? `${activeResultIndex + 1} of ${resultPages.length}` : "0 of 0"}
                    </span>
                    <button
                      type="button"
                      disabled={activeResultIndex >= resultPages.length - 1}
                      onClick={() => setActiveResultId(resultPages[activeResultIndex + 1]?.id)}
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        isDark
                          ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
                  {resultPages.map((page, index) => (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => setActiveResultId(page.id)}
                      className={`shrink-0 rounded-2xl border px-3 py-2 text-left text-xs transition ${
                        page.id === activeResultPage?.id
                          ? "border-sky-300 bg-sky-100 text-sky-800"
                          : isDark
                          ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <span className="font-semibold">Page {index + 1}</span>
                      <span className="ml-2 max-w-[220px] truncate align-bottom inline-block">
                        {page.title}
                      </span>
                    </button>
                  ))}
                </div>

                {activeResultPage ? (
                  <div className="space-y-4">
                    {activeResultPage.user ? (
                      <ResultCard
                        item={activeResultPage.user}
                        isDark={isDark}
                        onAction={handleAction}
                        onSaveFollowUp={handleSaveFollowUp}
                        isFollowUpSaved={Boolean(savedFollowUps[activeResultPage.user.id])}
                      />
                    ) : null}

                    {activeResultPage.assistant ? (
                      <ResultCard
                        item={activeResultPage.assistant}
                        isDark={isDark}
                        onAction={handleAction}
                        onSaveFollowUp={handleSaveFollowUp}
                        isFollowUpSaved={Boolean(savedFollowUps[activeResultPage.assistant.id])}
                      />
                    ) : null}
                  </div>
                ) : (
                  <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                    Run a prompt to create the first result page.
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div
                className={`rounded-[2rem] border p-5 shadow-md ${
                  isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
                }`}
              >
                <div className="mb-5">
                  <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    AI Signals
                  </h2>
                  <p className={`${isDark ? "text-slate-300" : "text-slate-600"} text-sm`}>
                    System-generated executive insights
                  </p>
                </div>

                <div className="space-y-4">
                  {dynamicInsights.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.title}
                        className={`rounded-3xl border p-4 ${
                          isDark
                            ? "border-slate-800 bg-slate-950/60"
                            : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`rounded-2xl p-3 ${item.tone}`}>
                            <Icon className="h-5 w-5" />
                          </div>

                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                                {item.title}
                              </h3>
                              <Badge className={item.tone}>{item.badge}</Badge>
                            </div>
                            <p className={`mt-2 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div
                className={`rounded-[2rem] border p-5 shadow-md ${
                  isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
                }`}
              >
                <div className="mb-4 flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Suggested next move</span>
                </div>
                <p className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {summary.highPriorityTickets.length > 0
                    ? `Prioritize ${summary.highPriorityTickets.length} high-priority maintenance ticket${summary.highPriorityTickets.length === 1 ? "" : "s"}, then follow up on ${formatMoney(summary.overduePayments, currency)} in overdue payments.`
                    : `Follow up on ${formatMoney(summary.pendingPayments, currency)} pending payments and monitor ${summary.openTickets.length} open maintenance ticket${summary.openTickets.length === 1 ? "" : "s"}.`}
                </p>
              </div>

              <div
                className={`rounded-[2rem] border p-5 shadow-md ${
                  isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
                }`}
              >
                <h2 className={`mb-4 text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  AI Confidence
                </h2>

                <div className="space-y-4">
                  {[
                    { label: "Collections analysis", value: "96%" },
                    { label: "Occupancy risk detection", value: "91%" },
                    { label: "Maintenance prioritization", value: "94%" },
                    { label: "Tenant draft generation", value: "98%" },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="mb-2 flex items-center justify-between">
                        <span className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                          {item.label}
                        </span>
                        <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {item.value}
                        </span>
                      </div>

                      <div className={`h-2 rounded-full ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
                        <div
                          className="h-2 rounded-full bg-gradient-to-r from-sky-500 to-orange-500"
                          style={{ width: item.value }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
